import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

/**
 * Color gradient from green (fast) to red (slow)
 */
const BUCKET_COLORS = {
  '0-50ms': '#22c55e',     // green-500
  '50-100ms': '#84cc16',   // lime-500
  '100-200ms': '#eab308',  // yellow-500
  '200-500ms': '#f97316',  // orange-500
  '500ms-1s': '#ef4444',   // red-500
  '1s-5s': '#dc2626',      // red-600
  '5s-30s': '#b91c1c',     // red-700
  '>30s': '#7f1d1d',       // red-900
};

/**
 * Order of buckets for display
 */
const BUCKET_ORDER = [
  '0-50ms',
  '50-100ms',
  '100-200ms',
  '200-500ms',
  '500ms-1s',
  '1s-5s',
  '5s-30s',
  '>30s',
];

/**
 * HoldTimeHistogram - Horizontal bar chart for hold time distribution
 *
 * @param {Object} props
 * @param {Object} props.histogram - Object with bucket keys and count values
 * @param {string} props.title - Chart title
 * @param {number} props.height - Chart height in pixels (default: 300)
 */
export default function HoldTimeHistogram({
  histogram = {},
  title = 'Hold Time Distribution',
  height = 300,
}) {
  // Transform histogram object to array format for recharts
  const data = BUCKET_ORDER
    .map((bucket) => ({
      bucket,
      count: histogram[bucket] || 0,
      color: BUCKET_COLORS[bucket],
    }))
    .filter((item) => item.count > 0 || true); // Include all buckets for context

  const total = data.reduce((sum, item) => sum + item.count, 0);

  if (total === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        {title && (
          <h3 className="text-sm font-semibold text-gray-700 mb-4">{title}</h3>
        )}
        <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
          <p className="text-sm">No hold time data available</p>
        </div>
      </div>
    );
  }

  const formatTooltip = (value, name, props) => {
    const percentage = ((value / total) * 100).toFixed(1);
    return [`${value} (${percentage}%)`, 'Connections'];
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {title && (
        <h3 className="text-sm font-semibold text-gray-700 mb-2">{title}</h3>
      )}
      <p className="text-xs text-gray-500 mb-4">
        Total: {total.toLocaleString()} connection releases
      </p>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={true} vertical={false} />
          <XAxis
            type="number"
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickLine={{ stroke: '#d1d5db' }}
            axisLine={{ stroke: '#d1d5db' }}
          />
          <YAxis
            type="category"
            dataKey="bucket"
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickLine={{ stroke: '#d1d5db' }}
            axisLine={{ stroke: '#d1d5db' }}
            width={70}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '12px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}
            formatter={formatTooltip}
            cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
          />
          <Bar dataKey="count" radius={[0, 4, 4, 0]}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="flex flex-wrap gap-2 mt-4 justify-center">
        {BUCKET_ORDER.map((bucket) => (
          <div key={bucket} className="flex items-center gap-1 text-xs">
            <div
              className="w-3 h-3 rounded"
              style={{ backgroundColor: BUCKET_COLORS[bucket] }}
            />
            <span className="text-gray-600">{bucket}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
